import { useState } from 'react'
import { useIntl } from 'react-intl'

import { Link } from '@tanstack/react-router'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { MessagesSquareIcon } from 'lucide-react'

import { AdminTable } from '@/components/atoms/AdminTable'
import { Icons } from '@/components/atoms/Icons'
import { AdminHeader } from '@/components/organisms/AdminHeader/AdminHeader.tsx'
import { type AvailableDate } from '@/pages/regions/useOperatingRegionsPage'
import { type OperatingRegion } from '@/state/regions/types'

const columnHelper = createColumnHelper<OperatingRegion>()

function createColumns(
  showTeamBreakdown: boolean,
  onToggleTeamBreakdown: () => void,
  showTransportRequestBreakdown: boolean,
  onToggleTransportRequestBreakdown: () => void,
  isHistoricalMode: boolean,
  selectedDate?: string
) {
  return [
    {
      header: 'Region',
      columns: [
        columnHelper.display({
          id: 'index',
          header: '#',
          cell: (info) => info.row.index + 1,
          meta: {
            classname: 'text-sm text-center px-4 border-l border-gray-300',
            headerClassName: 'text-center border-l border-gray-300',
          },
          size: 1,
        }),
        columnHelper.accessor((row) => row, {
          id: 'name',
          header: 'name',
          meta: {
            classname: 'font-semibold text-black whitespace-nowrap pr-2',
            headerClassName: '',
          },
          cell: (info) => (
            <Link to="/" search={{ central_id: info.getValue().piloteId }} target="_blank">
              {info.getValue().name}
            </Link>
          ),
          size: 200,
        }),
        columnHelper.accessor((row) => row, {
          id: 'flags',
          meta: {
            headerClassName: 'text-sm px-4 border-l border-r border-gray-300',
            classname: 'border-l border-r border-gray-300',
          },
          cell: (info) => {
            const {
              openForTransportRequests,
              isAutomatedAssignmentEnabled,
              waitTime,
              mobileTransportRequestsEnabled,
              assignment,
            } = info.getValue()

            const mobileRequestsEnabled =
              mobileTransportRequestsEnabled && openForTransportRequests && !waitTime?.exceedsThreshold

            return (
              <div className="flex w-full items-center justify-evenly">
                <div>
                  {mobileRequestsEnabled && <Icons.mobileOk className="size-4 h-fit shrink-0 fill-green-400" />}
                  {!mobileRequestsEnabled && (
                    <Icons.mobileNotConnected
                      className={`${!mobileTransportRequestsEnabled ? 'fill-gray-300' : !openForTransportRequests ? 'fill-red-500' : waitTime?.exceedsThreshold ? 'fill-orange-400' : 'fill-blue-600'} size-4 h-fit shrink-0`}
                    />
                  )}
                </div>
                <div title={assignment}>
                  {!isAutomatedAssignmentEnabled && <Icons.manualAssignation className="size-5 fill-gray-300" />}
                  {isAutomatedAssignmentEnabled && assignment === 'manual' && (
                    <Icons.manualAssignation className="size-5 fill-orange-500" />
                  )}
                  {isAutomatedAssignmentEnabled && assignment === 'solver' && (
                    <Icons.shootingStar className="size-5 fill-green-400" />
                  )}
                </div>
              </div>
            )
          },
          size: 1,
        }),
      ],
    },
    {
      header: 'Wait Time',
      columns: [
        columnHelper.accessor((row) => row.waitTime?.minimumInMinutes, {
          id: 'minimumInMinutes',
          header: 'min',
          meta: { classname: 'px-2 text-center', headerClassName: 'text-center text-[8px]' },
          cell: (info) => {
            if (info.row.original.waitTimeConfiguration?.type === 'increased') {
              return <span className="text-red-500">{info.getValue()}</span>
            }
            if (info.row.original.waitTime?.exceedsThreshold) {
              return <span className="text-orange-400">{info.getValue()}</span>
            }
            return info.getValue()
          },
          size: 1,
        }),
        columnHelper.accessor((row) => row.waitTime?.averageInMinutes, {
          id: 'averageInMinutes',
          header: 'avg',
          meta: { classname: 'px-2 text-center', headerClassName: 'text-center text-[8px]' },
          cell: (info) => {
            if (info.row.original.waitTimeConfiguration?.type === 'increased') {
              return <span className="text-red-500">{info.getValue()}</span>
            }
            if (info.row.original.waitTime?.exceedsThreshold) {
              return <span className="text-orange-400">{info.getValue()}</span>
            }
            return info.getValue()
          },
          size: 1,
        }),
        columnHelper.accessor((row) => row.waitTime?.maximumInMinutes, {
          id: 'maximumInMinutes',
          header: 'max',
          meta: {
            classname: 'px-2 text-center border-r border-gray-300',
            headerClassName: 'text-center text-[8px] border-r border-gray-300',
          },
          cell: (info) => {
            if (info.row.original.waitTimeConfiguration?.type === 'increased') {
              return <span className="text-red-500">{info.getValue()}</span>
            }
            if (info.row.original.waitTime?.exceedsThreshold) {
              return <span className="text-orange-400">{info.getValue()}</span>
            }
            return info.getValue()
          },
          size: 1,
        }),
      ],
    },
    {
      header: 'Transport Requests',
      columns: [
        columnHelper.accessor((row) => row.transportRequestsCount?.active, {
          id: 'transport-request-active',
          header: 'active',
          meta: {
            classname: 'font-semibold text-center',
            headerClassName: 'font-semibold text-center text-sm',
          },
          footer: (info) =>
            info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.active ?? 0), 0),
        }),
        ...(showTransportRequestBreakdown
          ? [
              columnHelper.accessor((row) => row.transportRequestsCount?.pending, {
                id: 'pending',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.pending ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.assigned, {
                id: 'assigned',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.assigned ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.allocated, {
                id: 'allocated',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.allocated ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.on_board, {
                id: 'on_board',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.on_board ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.cancelled, {
                id: 'cancelled',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.cancelled ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.unfindable, {
                id: 'unfindable',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.unfindable ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.transferred, {
                id: 'transferred',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.tansferred ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.transportRequestsCount?.completed, {
                id: 'completed',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px] px-1' },
                size: 1,
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.completed ?? 0), 0),
              }),
            ]
          : []),
        columnHelper.accessor((row) => row, {
          id: 'tr-total',
          header: () => (
            <span className="cursor-pointer" onClick={onToggleTransportRequestBreakdown}>
              {showTeamBreakdown ? '▸ total' : '◂ total'}
            </span>
          ),
          meta: {
            classname: 'font-semibold text-center border-r border-gray-300 pr-2',
            headerClassName: 'font-semibold text-center text-sm border-r border-gray-300 pr-2',
            footerClassName: 'font-semibold text-center border-r border-gray-300 pr-2',
          },
          cell: (info) => {
            const { transportRequestsCount } = info.getValue()
            return (
              <div className="flex w-full items-center justify-between">
                <div className="w-1/2 text-right">{transportRequestsCount?.total}</div>
                <div className="w-1/2 pl-1 text-right text-sm text-green-400">
                  {transportRequestsCount?.mobile > 0 ? transportRequestsCount?.mobile : ''}
                </div>
              </div>
            )
          },
          footer: (info) => (
            <div className="flex w-full items-center justify-between">
              <div className="w-1/2 text-right">
                {info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.total ?? 0), 0)}
              </div>
              <div className="w-1/2 pl-1 text-right text-sm text-green-400">
                {info.table.options.data.reduce((acc, row) => acc + (row.transportRequestsCount?.mobile ?? 0), 0)}
              </div>
            </div>
          ),
        }),
      ],
    },
    {
      header: 'Teams',
      columns: [
        columnHelper.accessor((row) => row.teams?.active, {
          id: 'team-active',
          header: 'active',
          meta: {
            classname: 'font-semibold text-center',
            headerClassName: 'font-semibold text-center text-sm',
          },
          footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.active ?? 0), 0),
        }),
        ...(showTeamBreakdown
          ? [
              columnHelper.accessor((row) => row.teams?.AVAILABLE_AT_COORDINATES, {
                id: 'GPS',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.teams?.AVAILABLE_AT_COORDINATES ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.AVAILABLE_IN_LOCATION, {
                id: 'Location',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.teams?.AVAILABLE_IN_LOCATION ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.AVAILABLE_IN_INSTITUTION, {
                id: 'Institution',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.teams?.AVAILABLE_IN_INSTITUTION ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.AVAILABLE_IN_REGION, {
                id: 'Region',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) =>
                  info.table.options.data.reduce((acc, row) => acc + (row.teams?.AVAILABLE_IN_REGION ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.ASSIGNED, {
                id: 'Assigned',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.ASSIGNED ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.ON_BREAK, {
                id: 'Break',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.ON_BREAK ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.IN_RETURN, {
                id: 'Returning',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.IN_RETURN ?? 0), 0),
              }),
              columnHelper.accessor((row) => row.teams?.DISSOLVED, {
                id: 'Dissolved',
                meta: { classname: 'text-center', headerClassName: 'text-center text-[8px]' },
                footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.DISSOLVED ?? 0), 0),
              }),
            ]
          : []),
        columnHelper.accessor((row) => row.teams?.total, {
          id: 'team-total',
          header: () => (
            <span className="cursor-pointer" onClick={onToggleTeamBreakdown}>
              {showTeamBreakdown ? '▸ total' : '◂ total'}
            </span>
          ),
          meta: {
            classname: 'font-semibold text-center',
            headerClassName: 'font-semibold text-center text-sm',
          },
          footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.total ?? 0), 0),
        }),
        columnHelper.accessor((row) => row.teams, {
          id: 'message-count',
          header: 'msgs',
          meta: {
            classname: 'text-center border-r border-gray-300 pr-2',
            headerClassName: 'text-center text-sm border-r border-gray-300 pr-2',
            footerClassName: 'text-center border-r border-gray-300 pr-2',
          },
          cell: (info) => {
            const teams = info.getValue()
            const count = teams?.messageCount ?? 0
            if (teams?.hasUnreadTeamMessages) {
              return <span className="font-semibold text-red-500">{count}</span>
            }
            if (teams?.hasUnreadAdminMessages) {
              return <span className="text-green-500">{count}</span>
            }
            return count
          },
          footer: (info) => info.table.options.data.reduce((acc, row) => acc + (row.teams?.messageCount ?? 0), 0),
        }),
      ],
    },
    {
      header: 'Links',
      columns: [
        columnHelper.accessor((row) => row.piloteId, {
          id: 'link-central',
          header: '',
          meta: { classname: 'text-center px-1', headerClassName: 'text-center' },
          cell: (info) => (
            <Link to="/" search={{ central_id: info.getValue() }} target="_blank" title="Centrale">
              <Icons.base className="inline-block size-4 fill-gray-600 hover:fill-blue-500" />
            </Link>
          ),
          size: 1,
        }),
        columnHelper.accessor((row) => row, {
          id: 'link-messaging',
          header: '',
          meta: { classname: 'text-center px-1', headerClassName: 'text-center' },
          cell: (info) => {
            const region = info.getValue()
            if (isHistoricalMode) {
              return (
                <Link
                  to="/regions/messages/$eveningId"
                  params={{ eveningId: String(region.displayEveningId) }}
                  search={{
                    regionName: region.name,
                    piloteRegionId: region.piloteId,
                    eveningDate: selectedDate,
                    regionId: region._id,
                  }}
                  target="_blank"
                  title="Messagerie">
                  <MessagesSquareIcon className="inline-block size-4 text-gray-600 hover:text-blue-500" />
                </Link>
              )
            }
            return (
              <Link
                to="/dashboard/$regionId/messages"
                params={{ regionId: region._id }}
                target="_blank"
                title="Messagerie">
                <MessagesSquareIcon className="inline-block size-4 text-gray-600 hover:text-blue-500" />
              </Link>
            )
          },
          size: 1,
        }),
        columnHelper.accessor((row) => row._id, {
          id: 'link-plans',
          header: '',
          meta: {
            classname: 'text-center px-1 border-r border-gray-300',
            headerClassName: 'text-center border-r border-gray-300',
          },
          cell: (info) => (
            <Link
              to="/assignments"
              search={{ region_id: info.getValue(), limit: undefined }}
              target="_blank"
              title="Plans">
              <Icons.shootingStar className="inline-block size-4 fill-gray-600 hover:fill-blue-500" />
            </Link>
          ),
          size: 1,
        }),
      ],
    },
  ]
}

export interface OperatingRegionsViewProps {
  regions: OperatingRegion[]
  selectedDate?: string
  isHistoricalMode: boolean
  availableDates: AvailableDate[]
  onDateChange: (date: string | undefined) => void
  onBackToPiloteClicked: () => void
}

export const OperatingRegionsView: React.FC<OperatingRegionsViewProps> = ({
  regions,
  selectedDate,
  isHistoricalMode,
  availableDates,
  onDateChange,
  onBackToPiloteClicked,
}) => {
  const intl = useIntl()
  const [showTeamBreakdown, setShowTeamBreakdown] = useState(false)
  const [showTransportRequestBreakdown, setShowTransportRequestBreakdown] = useState(false)

  const table = useReactTable({
    defaultColumn: { size: 100 },
    data: regions,
    columns: createColumns(
      showTeamBreakdown,
      () => {
        setShowTeamBreakdown(!showTeamBreakdown)
      },
      showTransportRequestBreakdown,
      () => {
        setShowTransportRequestBreakdown(!showTransportRequestBreakdown)
      },
      isHistoricalMode,
      selectedDate
    ),
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="flex h-full flex-col">
      <AdminHeader title="Tableau de bord" onBackToPiloteClicked={onBackToPiloteClicked}>
        <select
          value={selectedDate ?? ''}
          onChange={(e) => {
            onDateChange(e.target.value || undefined)
          }}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none">
          <option value="">{intl.formatMessage({ id: 'stats.now' })}</option>
          {availableDates.map(({ date, label }) => (
            <option key={date} value={date}>
              {label}
            </option>
          ))}
        </select>
      </AdminHeader>
      <div className="flex-1 overflow-auto p-4 pl-16">
        {regions.length === 0 ? (
          <div className="text-gray-500">
            {isHistoricalMode ? 'Aucune soirée trouvée pour cette date' : 'Aucune région en opération'}
          </div>
        ) : (
          <AdminTable table={table} showFooter />
        )}
      </div>
    </div>
  )
}
